// GS1 element strings in templates: a barcode's value is written with the AIs in brackets,
// `(00)373500341500000016` or `(01)07350034150001(10)K49O8610(21)7`. This module parses that
// form, knows which AIs are fixed-length (no FNC1 separator needed after them) and produces
// the ZPL ^BC data (subset switching, FNC1 as >8) and the human-readable text.
/** Fixed-length AIs per GS1 General Specifications §7.8.5 (the predefined-length table). */
const FIXED = {
    "00": 18, "01": 14, "02": 14, "03": 14, "04": 16, "11": 6, "12": 6, "13": 6, "14": 6, "15": 6, "16": 6, "17": 6, "18": 6, "19": 6, "20": 2,
    "31": 6, "32": 6, "33": 6, "34": 6, "35": 6, "36": 6, "41": 13,
};
export function parseElementString(s) {
    const out = [];
    const re = /\((\d{2,4})\)([^(]*)/g;
    let consumed = 0;
    for (const m of s.trim().matchAll(re)) {
        const ai = m[1];
        const value = m[2].trim();
        const key2 = ai.slice(0, 2);
        const fixedLen = FIXED[ai] ?? FIXED[key2] ?? null;
        out.push({ ai, value, fixed: fixedLen !== null });
        consumed += m[0].length;
    }
    if (out.length === 0 || consumed < s.trim().length - 2)
        return null;
    return out;
}
/** The digits-and-letters payload without brackets, with the ASCII GS (0x1D) where a separator
 *  is needed — the form bwip-js and most scanners expect for `gs1-128` when parsed. */
export function elementStringToRaw(segs) {
    return segs.map((s, i) => `${s.ai}${s.value}${!s.fixed && i < segs.length - 1 ? "\u001d" : ""}`).join("");
}
/** Human-readable interpretation: `(01)07350034150001(10)K49O8610`. */
export function elementStringHri(segs, spaced = false) {
    return segs.map((s) => `(${s.ai})${spaced ? " " : ""}${s.value}`).join(spaced ? " " : "");
}
/** Code 128 data for ZPL ^BC with the subset-switch and FNC1 escapes of ^BC (the `>` hints:
 *  `>;` subset C, `>6` subset B, `>8` FNC1). Digit runs of even length go in subset C; the
 *  AI pair always does. A FNC1 follows every variable-length AI that is not last. */
export function elementStringToZpl(segs) {
    let out = ">;>8";
    let subset = "C";
    const emit = (text) => {
        let i = 0;
        while (i < text.length) {
            // Longest run of digits of even length from here.
            let j = i;
            while (j < text.length && /\d/.test(text[j]))
                j++;
            let digits = j - i;
            if (digits >= 2) {
                if (digits % 2 === 1)
                    digits--;
                if (subset !== "C") {
                    out += ">;";
                    subset = "C";
                }
                out += text.slice(i, i + digits);
                i += digits;
            }
            else {
                if (subset !== "B") {
                    out += ">6";
                    subset = "B";
                }
                out += text[i].replace(/[\^~\\]/g, " ").replace(">", ">>");
                i++;
            }
        }
    };
    segs.forEach((s, idx) => {
        emit(s.ai + s.value);
        if (!s.fixed && idx < segs.length - 1)
            out += ">8";
    });
    return out;
}
/** Warnings for a GS1 element string: bad lengths, non-digits in numeric AIs, SSCC/GTIN check digit. */
export function checkElementString(segs) {
    const w = [];
    for (const s of segs) {
        const fixedLen = FIXED[s.ai] ?? FIXED[s.ai.slice(0, 2)];
        if (fixedLen && s.value.length !== fixedLen)
            w.push(`AI (${s.ai}) ska ha ${fixedLen} tecken, har ${s.value.length}`);
        if (["00", "01", "02", "11", "13", "15", "17", "30", "37"].includes(s.ai) && !/^\d*$/.test(s.value))
            w.push(`AI (${s.ai}) får bara innehålla siffror`);
        if ((s.ai === "00" || s.ai === "01") && /^\d+$/.test(s.value) && s.value.length === fixedLen && !checkDigitOk(s.value))
            w.push(`AI (${s.ai}): kontrollsiffran stämmer inte`);
        if (s.ai === "10" && s.value.length > 20)
            w.push("AI (10) batch får vara högst 20 tecken");
        if (s.ai === "21" && s.value.length > 20)
            w.push("AI (21) serienummer får vara högst 20 tecken");
    }
    return w;
}
export function checkDigitOk(digits) {
    const body = digits.slice(0, -1);
    let sum = 0;
    for (let i = 0; i < body.length; i++) {
        const n = Number(body[body.length - 1 - i]);
        sum += n * (i % 2 === 0 ? 3 : 1);
    }
    return (10 - (sum % 10)) % 10 === Number(digits[digits.length - 1]);
}
//# sourceMappingURL=gs1.js.map